import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useTranslation } from "react-i18next";
import FancyLoader from "@/components/common/fancyLoader";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import { useAlert } from "@/hooks/useAlert";
import { ChatDetails, GptDetails } from "@/types/chat";
import { FileDeleteRequest, FileUploadType } from "@/types/common/files";
import { apiFetch } from "@/utils/common/api";
import { FileUploadTypes } from "@/utils/files";
import { FormatDataSize } from "@/utils/library/formatters";
import { ToDateTimeLabel } from "@/utils/library/time";
import { Progress } from "@nextui-org/react";
import { upload } from "@vercel/blob/client";

type ChatFileUploadProps = {
  details: ChatDetails | GptDetails;
  conversationId: string;
  fileType: FileUploadTypes;
  handleFileAction?: (action: "delete" | "add", url: string) => Promise<void>;
};

const ChatFileUpload: React.FC<ChatFileUploadProps> = ({
  details,
  conversationId,
  fileType,
  handleFileAction,
}) => {
  const { setAlert } = useAlert();
  const { t } = useTranslation("onboarding");
  const { t: ct } = useTranslation("common");

  const [isLoading, setIsLoading] = useState(true);
  const [files, setFiles] = useState<FileUploadType[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const fetchFiles = async () => {
    const fileList = await apiFetch<void, FileUploadType[]>(
      `/api/public/chat/${conversationId}/files/${fileType}/list`,
      "GET",
      {
        headers: {
          "x-org-id": details.orgId,
          "x-agent-id": details.agentId,
        },
      }
    );

    setFiles(fileList ?? []);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const uploadFile = async (
    fileToUpload: Omit<FileUploadType, "file"> & { file: File }
  ) => {
    try {
      const response = await upload(fileToUpload.pathname, fileToUpload.file, {
        access: "public",
        handleUploadUrl: `/api/public/chat/${conversationId}/files/${fileType}/upload`,
        onUploadProgress(e) {
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.pathname === fileToUpload.pathname
                ? { ...f, progress: Number(e.percentage.toFixed(0)) }
                : f
            )
          );
        },
        clientPayload: JSON.stringify({
          "x-org-id": details.orgId,
          "x-agent-id": details.agentId,
        }),
      });

      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.pathname === fileToUpload.pathname
            ? { ...f, status: "uploaded", url: response.url }
            : f
        )
      );

      if (handleFileAction) {
        await handleFileAction("add", response.url);
      }
    } catch (error) {
      console.error("Error uploading file", error);
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.pathname === fileToUpload.pathname ? { ...f, status: "error" } : f
        )
      );
    }
  };

  //This function will only be called with the latest file(s)
  //  If one or more were uploaded before they will not be included
  const handleChange = async (fileList: FileList) => {
    if (isUploading) {
      return;
    }

    const filesToUpload = Array.from(fileList).filter(
      (file) => !files.some((f) => f.pathname === file.name)
    );

    if (
      filesToUpload.length + files.length > details.maxFiles ||
      filesToUpload.length === 0
    ) {
      return;
    }

    const newFiles: (Omit<FileUploadType, "file"> & { file: File })[] =
      filesToUpload.map((file) => ({
        file,
        size: file.size,
        pathname: `${details.orgId}/${fileType}/${details.agentId}/${conversationId}/${file.name}`,
        status: "uploading" as "uploading",
        progress: -1,
        url: "",
        downloadUrl: "",
        updatedAt: new Date().toISOString(),
      }));

    setFiles([...files, ...newFiles]);

    setIsUploading(true);

    await Promise.all(newFiles.map(async (file) => uploadFile(file)));

    setIsUploading(false);
  };

  if (isLoading) {
    return (
      <div>
        <FancyLoader isSendingData />
        <div className={clsx("text-center", "text-2xl")}>
          {t("loadingFiles")}
        </div>
      </div>
    );
  }

  return (
    <div>
      {files &&
        files.length > 0 &&
        files.reduce((acc, file) => acc + file.size, 0) > 2 * 1024 * 1024 && (
          <p className="mb-4">{t("largeFileNotice")}</p>
        )}
      <div
        className={clsx(
          "mb-4",
          "grid",
          "grid-cols-1",
          "gap-2",
          "md:grid-cols-2",
          "xl:grid-cols-3"
        )}
      >
        {files.map((file, index) => {
          const filename = file.pathname.split("/").pop();
          return (
            <div
              key={index}
              className={clsx(
                "bg-panel",
                "p-4",
                "grid",
                "gap-2",
                "rounded-lg",
                "shadow-md"
              )}
            >
              <h2>
                {filename}
                <span className={clsx("float-right", "text-xs", "text-600")}>
                  {FormatDataSize(file.size)}
                </span>
                {file.status === "uploaded" && (
                  <div className={clsx("text-xs", "text-600")}>
                    {ToDateTimeLabel(file.updatedAt, ct, details.localization)}
                  </div>
                )}
              </h2>
              {file.status !== "uploaded" && (
                <div>
                  {file.status === "uploading" ? (
                    <>
                      <Progress
                        size="lg"
                        classNames={{
                          track: "h-7",
                          indicator: "bg-lula",
                        }}
                        isStriped={file.progress < 0}
                        aria-label="Uploading..."
                        value={file.progress}
                        isIndeterminate={file.progress < 0}
                      />
                      {file.progress >= 0 && (
                        <div className={clsx("mt-[-1.75rem]", "text-center")}>
                          <span
                            className={clsx(
                              "px-2",
                              "py-1",
                              "rounded-full",
                              "bg-panel",
                              "shadow-inner",
                              "text-xs",
                              "relative",
                              "z-20"
                            )}
                          >
                            {file.progress}%
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    t("fileError")
                  )}
                </div>
              )}
              <div className={clsx("grid", "gap-2", "grid-cols-2")}>
                <Button
                  type="tertiary"
                  wide
                  compact
                  disabled={file.status === "uploading"}
                  onClick={async () => {
                    if (!file.url) {
                      setFiles((prevFiles) =>
                        prevFiles.filter((f) => f.pathname !== file.pathname)
                      );

                      return;
                    }

                    setIsLoading(true);

                    const deleteResponse = await apiFetch<
                      FileDeleteRequest,
                      { success: boolean }
                    >(
                      `/api/public/chat/${conversationId}/files/${fileType}`,
                      "DELETE",
                      {
                        headers: {
                          "x-org-id": details.orgId,
                          "x-agent-id": details.agentId,
                        },
                        body: {
                          url: file.url,
                        },
                      }
                    );

                    setIsLoading(false);

                    if (deleteResponse && deleteResponse.success) {
                      setFiles((prevFiles) =>
                        prevFiles.filter((f) => f.pathname !== file.pathname)
                      );

                      if (handleFileAction) {
                        await handleFileAction("delete", file.url);
                      }
                    } else {
                      setAlert({
                        message: t("fileDeleteError"),
                        status: "error",
                        secondsToClose: 10,
                      });
                    }
                  }}
                >
                  {ct("remove")}
                </Button>
                {file.url && (
                  <Button
                    type="tertiary"
                    wide
                    compact
                    disabled={file.status !== "uploaded"}
                    href={file.url}
                    target="_blank"
                  >
                    {ct("view")}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <FileUploader
        handleChange={handleChange}
        name="file"
        types={details.fileTypes}
        multiple
        disabled={details.maxFiles <= files.length}
        hoverTitle={t("dropFilesHere")}
        maxSize={5}
        onSizeError={() =>
          setAlert({
            message: t("fileTooBig"),
            status: "error",
            secondsToClose: 10,
          })
        }
      >
        <div
          className={clsx(
            "border-2",
            "border-dashed",
            "border-lula",
            "p-4",
            "rounded-md",
            "mb-2",
            "bg-panel",
            "text-center",
            "shadow-[inset_0_0_4px_rgba(0,0,0,0.6)]",
            "cursor-pointer"
          )}
        >
          <div>{t("uploadFilesHere")}</div>
          <div className={clsx("text-600", "mt-2")}>
            {details.fileTypes.includes("PDF") && (
              <Icon icon="file-pdf" size={2} />
            )}
            {details.fileTypes.includes("DOCX") && (
              <Icon icon="file-doc" size={2} className="ml-2" />
            )}
            {details.fileTypes.includes("XLSX") && (
              <Icon icon="file-excel" size={2} className="mx-2" />
            )}
            {(details.fileTypes.includes("JPG") ||
              details.fileTypes.includes("JPEG") ||
              details.fileTypes.includes("PNG") ||
              details.fileTypes.includes("GIF") ||
              details.fileTypes.includes("WEBP") ||
              details.fileTypes.includes("BMP")) && (
              <Icon icon="file-image" size={2} className="ml-2" />
            )}
          </div>
          <div className="mt-4">
            {t("youHaveUploaded", {
              count: files.length,
              max: details.maxFiles,
            })}
          </div>
        </div>
      </FileUploader>
    </div>
  );
};

export default ChatFileUpload;
